<script>
import { CONVERSATION_STATUS } from 'shared/constants/messages';

export default {
  name: 'StatusBadge',
  props: {
    status: {
      type: String,
      required: true,
      validator: value => Object.values(CONVERSATION_STATUS).includes(value),
    },
    size: {
      type: String,
      default: 'small',
      validator: value => ['small', 'medium'].includes(value),
    },
  },
  computed: {
    statusConfig() {
      const configs = {
        [CONVERSATION_STATUS.OPEN]: {
          label: 'CHAT_LIST.CHAT_STATUS.OPEN',
          class: 'status-badge--open',
        },
        [CONVERSATION_STATUS.RESOLVED]: {
          label: 'CHAT_LIST.CHAT_STATUS.RESOLVED',
          class: 'status-badge--resolved',
        },
        [CONVERSATION_STATUS.PENDING]: {
          label: 'CHAT_LIST.CHAT_STATUS.PENDING',
          class: 'status-badge--pending',
        },
        [CONVERSATION_STATUS.SNOOZED]: {
          label: 'CHAT_LIST.CHAT_STATUS.SNOOZED',
          class: 'status-badge--snoozed',
        },
      };
      return configs[this.status] || configs[CONVERSATION_STATUS.OPEN];
    },
    badgeClasses() {
      return [
        'status-badge',
        this.statusConfig.class,
        `status-badge--${this.size}`,
      ];
    },
  },
};
</script>

<template>
  <span :class="badgeClasses">
    <span class="status-badge__dot" />
    {{ $t(statusConfig.label) }}
  </span>
</template>

<style lang="scss" scoped>
// Pill com ponto de status, no lugar do retangulo de raio 4px com borda.
//
// O que estava errado antes:
//   - `px-1 py-0.5` e `leading-3` deixavam o texto encostado nas bordas.
//   - `mb-0.5` punha margem num badge, desalinhando-o de tudo ao lado.
//   - `snoozed` usava bg-woot-25 com border-woot-200, quase branco sobre
//     branco: parecia texto azul soltono meio da tela, nao um badge.
//   - cada estado pegava um degrau diferente da rampa (100, 25...), entao os
//     quatro tinham peso visual desigual.
//
// Agora os quatro seguem o mesmo par (50 de fundo, 700 de texto) e o ponto
// herda a cor do texto via currentColor -- um sinal que se le antes da
// palavra, o que importa numa lista longa de conversas.
.status-badge {
  @apply inline-flex items-center gap-1.5 font-medium rounded-full
    whitespace-nowrap;

  &__dot {
    @apply w-1.5 h-1.5 rounded-full shrink-0;
    background-color: currentColor;
  }

  &--small {
    @apply h-5 px-2 text-xxs;
  }

  &--medium {
    @apply h-6 px-2.5 text-xs;
  }

  &--open {
    @apply bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300;
  }

  // O unico estado neutro da escala, e por isso o unico que precisa de
  // contorno: `--fd-surface-sunken` e `--fd-surface-hover` valem os dois
  // `241 243 245` no tema claro, entao sobre qualquer linha ou item de menu em
  // hover este pill perdia o preenchimento e virava texto solto. Os outros tres
  // estados sao tingidos e nao colidem com fundo de superficie.
  &--resolved {
    @apply bg-surface-sunken text-muted border border-line
      dark:bg-surface-sunken dark:text-muted;
  }

  &--pending {
    @apply bg-amber-50 text-amber-800;
  }

  &--snoozed {
    @apply bg-brand-subtle text-brand-text dark:bg-brand-subtle
      dark:text-brand-text;
  }
}
</style>
